import { motion } from "framer-motion";
import { MessageSquareCodeIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { ReactNode, useMemo } from "react";

import { InputVariableSelect } from "./InputVariableSelect";

import SettingsSection from "@/components/Playground/Messages/MessagesHeader/MessagesSettings/SettingsSection";
import { usePlayground } from "@/components/Playground/playground-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface ChatSettingsProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ChatSettings = observer(({ trigger, open, onOpenChange }: ChatSettingsProps) => {
  const { chat_settings, detectedInputVariables, setChatSettings } = usePlayground();

  // Transform string array to Variable objects
  const detectedVariables = useMemo(
    () =>
      detectedInputVariables.map(key => ({
        id: key,
        key,
        value: "",
      })),
    [detectedInputVariables],
  );

  const inputVariablesInjection = useMemo(
    () => detectedVariables.filter(v => v.key !== chat_settings?.user_input_variable),
    [detectedVariables, chat_settings],
  );

  const inputVariablesUserInput = useMemo(
    () => detectedVariables.filter(v => v.key !== chat_settings?.injection_variable),
    [detectedVariables, chat_settings],
  );

  const selectedInjectionVariable = useMemo(
    () => detectedVariables.find(v => v.key === chat_settings?.injection_variable),
    [detectedVariables, chat_settings],
  );
  const selectedUserInputVariable = useMemo(
    () => detectedVariables.find(v => v.key === chat_settings?.user_input_variable),
    [detectedVariables, chat_settings],
  );

  const handleInjectionVariableChange = (key: string | null) => {
    setChatSettings({ ...chat_settings, injection_variable: key });
  };

  const handleUserInputVariableChange = (key: string | null) => {
    setChatSettings({ ...chat_settings, user_input_variable: key });
  };

  const defaultTrigger = (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: [0.8, 1.1, 1] }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, scale: { times: [0, 0.6, 1] } }}
    >
      <Button
        variant="ghost"
        size="sm"
        className="h-8 rounded-full px-2.5 text-gray-700 transition-colors hover:bg-gray-100"
      >
        <MessageSquareCodeIcon className="mr-1.5 h-4 w-4 text-gray-500" />
        <span className="whitespace-nowrap text-sm">Chat Settings</span>
      </Button>
    </motion.div>
  );

  const shouldRenderTrigger = trigger !== undefined || open === undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {shouldRenderTrigger && <DialogTrigger asChild>{trigger ?? defaultTrigger}</DialogTrigger>}

      <DialogContent className="w-full max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Chat Settings</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Customize your chat settings
          </DialogDescription>
        </DialogHeader>

        <div className="w-full space-y-6 overflow-y-auto px-6 pb-2">
          <SettingsSection
            title="Message History Injection"
            info="Select the variable to use for the message history injection"
          >
            <InputVariableSelect
              vars={inputVariablesInjection}
              selected={selectedInjectionVariable}
              onChange={handleInjectionVariableChange}
              defaultMessage="Append to end of prompt"
            />
          </SettingsSection>
          <SettingsSection title="User Input" info="Select the variable to use for the user input">
            <InputVariableSelect
              vars={inputVariablesUserInput}
              selected={selectedUserInputVariable}
              onChange={handleUserInputVariableChange}
              defaultMessage="New message each turn"
            />
          </SettingsSection>
        </div>
      </DialogContent>
    </Dialog>
  );
});
